import { useState } from 'react';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { TextSmRg, Typography } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppToggleButton } from '@/UI/AppToggleButtons/AppToggleButton';
import { ImageIcon, PencilLineIcon, UploadIcon, ThreeDotsVerticalIcon } from '@/UI/Icons';
import { FlexCenHorVer, FlexCenVer, FlexCol } from '@/UI/styles';
import { useResolvedAssets } from '@/state/workflow.state';
import { assetThumbnailUrl } from '@/utils/assetUrls';
import { color } from '../../../colors';
import LoRAModal from './LoRAModal';

function LoRA({ lora, updateLora, deleteLora, setSelectedLora, container }) {
  const editable = container === 'drawer';
  const coverSrc = assetThumbnailUrl(lora.coverImage, useResolvedAssets(), { type: 'image' });
  const [open, setOpen] = useState(false);
  const [imageHover, setImageHover] = useState(false);
  const [loraHover, setLoraHover] = useState(false);
  const [nameHover, setNameHover] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const extension = '.safetensors';
  const filename =
    lora.file?.indexOf(extension) > 0
      ? lora.file.substring(0, lora.file.indexOf(extension) + extension.length)
      : lora.file;
  const handleEditLora = () => {
    setOpen(true);
  };

  const handleDeleteLora = () => {
    deleteLora(lora.id);
  };

  const menu = [
    { biEventKey: { menu: MENU_NAMES.LORA, action: 'edit' }, name: 'Edit', action: handleEditLora },
    { biEventKey: { menu: MENU_NAMES.LORA, action: 'delete' }, name: 'Delete', action: handleDeleteLora },
  ];

  // modal related

  const handleClose = () => {
    setSelectedLora(lora);
    setOpen(false);
  };

  return (
    <>
      <FlexCenVer
        id="lora-container"
        sx={{
          p: 0.5,
          pl: container === 'drawer' ? 0.5 : 1,
          border: container === 'drawer' ? '1px solid' : 'none',
          borderColor: color.White64_T,
          borderRadius: container === 'drawer' ? 1 : 0,

          my: 1,

          cursor: editable ? 'default' : 'pointer',
        }}
        onMouseEnter={() => setLoraHover(true)}
        onMouseLeave={() => setLoraHover(false)}
      >
        <Box
          id="lora-cover-image-container"
          sx={{
            width: container === 'drawer' ? '35px' : '40px',
            height: container === 'drawer' ? '35px' : '40px',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {coverSrc ? (
            <img src={coverSrc} alt="LoRA Cover Image" width="100%" height="100%" style={{ objectFit: 'cover' }} />
          ) : (
            <FlexCenHorVer
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: 1,
                border: '1px solid',
                borderColor: color.White64_T,
                cursor: 'pointer',
                position: 'relative',
              }}
              onMouseEnter={() => {
                if (editable) {
                  setImageHover(true);
                  setLoraHover(false);
                }
              }}
              onMouseLeave={() => {
                if (editable) {
                  setImageHover(false);
                  setLoraHover(true);
                }
              }}
              onClick={() => setOpen(true)}
            >
              <UploadIcon
                height={20}
                width={20}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  opacity: imageHover ? 1 : 0,
                  transition: 'opacity 0.1s ease-in-out',
                }}
              />
              <ImageIcon
                height={20}
                width={20}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  opacity: imageHover ? 0 : 1,
                  transition: 'opacity 0.1s ease-in-out',
                }}
              />
            </FlexCenHorVer>
          )}
        </Box>
        <FlexCol
          id="lora-name-url-container"
          sx={{
            ml: 1,
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: 0.5,
          }}
        >
          <Box
            id="lora-name-container"
            onMouseEnter={() => {
              if (editable) {
                setNameHover(true);
                setLoraHover(false);
              }
            }}
            onMouseLeave={() => {
              if (editable) {
                setNameHover(false);
                setLoraHover(true);
              }
            }}
          >
            <TextSmRg fontWeight="bold" sx={{ lineHeight: 1 }}>
              {lora.name}
            </TextSmRg>
            <PencilLineIcon
              width={12}
              height={12}
              style={{
                transform: 'translateY(1px)',
                cursor: 'pointer',
                opacity: nameHover ? 1 : 0,
                transition: 'opacity 0.1s ease-in-out',
                marginLeft: 4,
              }}
              onClick={() => setOpen(true)}
            />
          </Box>
          <Tooltip title={lora.url} placement="top" enterDelay={300}>
            <Typography variant="body-std-rg" color={color.White64_T} sx={{ fontSize: '10px', fontStyle: 'italic' }}>
              {lora.file ? (
                filename.length > 30 ? (
                  filename.substring(0, 27) + '...'
                ) : (
                  filename
                )
              ) : (
                <FlexCenVer sx={{ gap: 1 }}>
                  <span>No File</span>
                  {editable && (
                    <span
                      style={{
                        opacity: loraHover ? 1 : 0,
                        transition: 'opacity 0.1s ease-in-out',
                      }}
                    >
                      <ButtonTextPlain variant="body-xs-rg" onClick={() => setOpen(true)}>
                        Upload
                      </ButtonTextPlain>
                    </span>
                  )}
                </FlexCenVer>
              )}
            </Typography>
          </Tooltip>
        </FlexCol>
        <FlexCenVer sx={{ gap: 1, ml: 'auto' }}>
          {editable && (
            <>
              <AppToggleButton
                value="lora-actions"
                selected={Boolean(anchorEl)}
                isIcon
                btnW={28}
                btnH={28}
                aria-label="LoRA actions"
                aria-haspopup="menu"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ border: 'none' }}
              >
                <ThreeDotsVerticalIcon style={{ color: color.White64_T }} />
              </AppToggleButton>
              <AppContextMenu
                items={menu}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                width="120px"
              />
            </>
          )}
        </FlexCenVer>
      </FlexCenVer>
      <LoRAModal open={open} onClose={handleClose} lora={lora} updateLora={updateLora} />
    </>
  );
}

export default LoRA;
